import type { JSX } from "react";
import { useNavigate } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { useCurator } from "../useCurator.js";

/**
 * Чат с куратором — честная ссылка в Telegram. AI-чат у нас пока нет: заглушать
 * его эмулированными репликами неправильно, куратор — живой редактор, и
 * разговор должен вестись с ним напрямую в Telegram.
 *
 * Экран — переходник: объясняет, кто будет отвечать, и даёт кнопку «написать
 * в Telegram». Внутри Telegram Mini App открывается через openTelegramLink.
 */
export function CuratorChat(): JSX.Element {
  const navigate = useNavigate();
  const c = useCurator();

  const openTelegram = (): void => {
    const url = `https://t.me/${c.username}`;
    const tg = (
      window as {
        Telegram?: {
          WebApp?: {
            openTelegramLink?: (u: string) => void;
            openLink?: (u: string) => void;
          };
        };
      }
    ).Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else if (tg?.openLink) tg.openLink(url);
    else window.open(url, "_blank", "noreferrer");
  };

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="совет · Telegram" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[20px]">
        <button
          type="button"
          onClick={() => {
            navigate(-1);
          }}
          className="self-start editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← назад
        </button>

        <div className="mt-[24px] flex items-center gap-[14px]">
          <div
            className="relative h-[56px] w-[56px] flex-none overflow-hidden rounded-full"
            style={{ background: "linear-gradient(180deg, #1a1030, #0b0614)" }}
          >
            <span
              className="absolute h-[12px] w-[12px] rounded-full"
              style={{
                background: "#c8a8ff",
                left: "22%",
                top: "18%",
                filter: "blur(1.5px)",
                opacity: 0.85,
              }}
            />
          </div>
          <div className="min-w-0">
            <div className="font-display font-light text-[24px] leading-[1.1] text-ink tracking-[-0.02em]">
              {c.firstName} <em className="italic text-velvet">{c.initial}</em>
            </div>
            <div className="editorial-meta text-ink/55">{c.letterRole}</div>
          </div>
        </div>

        <div className="mt-[26px] h-px bg-ink/14" />

        <h2 className="mt-[26px] font-display font-light leading-[1.05] tracking-[-0.02em] text-ink text-[34px]">
          Разговор
          <br />
          <em className="italic text-velvet">вживую.</em>
        </h2>

        <p className="mt-[16px] font-display italic font-light text-[15px] leading-[1.5] text-ink/75">
          {c.firstName} отвечает в Telegram — не внутри этого приложения, а как живой редактор.
          Напишите напрямую: так ваши слова дойдут тем же вечером, а не сгорят в боте.
        </p>

        <ul className="mt-[18px] space-y-[10px] font-display italic font-light text-[14px] leading-[1.5] text-ink/70">
          {c.strengths.map((s) => (
            <li key={s} className="flex gap-[10px]">
              <span aria-hidden className="mt-[8px] h-[1px] w-[14px] flex-none bg-accent" />
              <span>{s}</span>
            </li>
          ))}
        </ul>

        <div className="mt-[24px] rounded-[12px] border border-ink/12 bg-paper-soft px-[16px] py-[14px] font-display italic font-light text-[14px] leading-[1.5] text-ink/65">
          В Telegram {c.firstName} пишет как человек — без автоответов. Если будет долго не
          отвечать, напомните через пару часов: редакторы работают по очереди.
        </div>
      </div>

      <div className="px-[22px] pb-[24px] pt-[16px]">
        <button
          type="button"
          onClick={openTelegram}
          className="w-full flex items-baseline justify-between border-t border-ink/22 pt-[14px] font-display italic font-light text-[22px] tracking-[-0.01em] text-ink transition-opacity hover:opacity-80"
        >
          <span>написать {c.footerLabel} в telegram</span>
          <span className="text-accent">→</span>
        </button>
        <div className="mt-[10px] editorial-meta text-ink/45">@{c.username}</div>
      </div>
    </Screen>
  );
}
